import React, { useEffect, useState, useMemo } from "react";
import { Box, Menu, Popover } from "@mantine/core";
import { useSelector } from "react-redux";
import { useDisclosure } from "@mantine/hooks";
import swal from "sweetalert";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Icon from "./icons";
import WeekPicker from "./WeekPicker";
import css from "./Scheduler.module.css";
import TimesheetsTable, { aggregateTimesheets } from "./TimesheetsTable";
import TimesheetDetailDrawer from "./TimesheetDetailDrawer";
import MissedShiftDrawer from "./MissedShiftDrawer";
import TimeEntryEditDrawer from "./TimeEntryEditDrawer";
import ExportPeriodModal from "./ExportPeriodModal";
import EvvRecordDrawer from "./EvvRecordDrawer";
import { downloadEvvRecords } from "../../../components/EvvRecords/exportEvv";
import { getAllAttendenceStaff, fetchAttendanceRange } from "../../../services/staff";
import schedulerService from "../../../services/scheduler";
import { startOfWeek, addDays, weekLabel, isoDate } from "./scheduleUtils";

// Admin "Timesheets" — the single home for attendance: per-caregiver hours +
// approval, per-visit EVV detail/edit (in the detail drawer), and EVV export
// (in the Export dropdown). Reuses the proven attendance data + modals.
export default function TimesheetsAdmin() {
  const [currentPage] = useState(1);

  // Weekly calendar nav (matches the Scheduler design): navigate one week at a
  // time via the WeekPicker. range = the visible week.
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [datePop, setDatePop] = useState(false);
  const [dpMonth, setDpMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const range = useMemo(() => [moment(weekStart), moment(addDays(weekStart, 6)).endOf("day")], [weekStart]);

  const [isAddModalOpen, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [addPrefill, setAddPrefill] = useState(null); // seed for MissedShiftDrawer when resolving a specific missed shift
  const [editRecord, setEditRecord] = useState(null); // the single time entry being edited
  const [evvRecord, setEvvRecord] = useState(null); // the entry whose EVV record is being edited
  const [backRow, setBackRow] = useState(null); // caregiver row to return to when leaving an editor
  const [exportTarget, setExportTarget] = useState(null); // { kind: 'timesheets'|'evv', format } -> opens period picker
  const [refresh, setRefresh] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  // Scheduled shifts for the visible week, joined to attendance so the
  // timesheet can compare scheduled vs actual and flag late/missed shifts.
  const [weekShifts, setWeekShifts] = useState([]);

  // Overtime config (from Scheduler settings): threshold hours + whether to surface OT.
  const otThreshold = settings?.rules?.overtimeThreshold ?? 40;
  const otAlerts = settings?.rules?.overtimeAlerts !== false;

  const rangeLabel = weekLabel(weekStart);

  useEffect(() => {
    schedulerService.getSchedulerSettings().then((s) => setSettings(s || null)).catch(() => {});
  }, []);

  const { attendanceStaff } = useSelector((state) => state?.staff || {});
  // Only caregivers with real logged time — drops stray/zero-hour entries
  // (e.g. admin accounts with a 0.0h clock record).
  const rows = useMemo(
    () => aggregateTimesheets(attendanceStaff, otThreshold, weekShifts).filter((r) => r.hours > 0 || r.exceptions > 0),
    [attendanceStaff, otThreshold, weekShifts]
  );

  const kpis = useMemo(() => {
    let hours = 0;
    let ot = 0;
    let exceptions = 0;
    let approved = 0;
    rows.forEach((r) => {
      hours += r.hours;
      ot += r.ot;
      exceptions += r.exceptions;
      if (r.status === "approved") approved += 1;
    });
    return { hours, ot, exceptions, approved, total: rows.length };
  }, [rows]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await getAllAttendenceStaff(currentPage, range, "");
        const s = await schedulerService.getAppointments({ from: isoDate(weekStart), to: isoDate(addDays(weekStart, 6)) });
        setWeekShifts(Array.isArray(s) ? s : []);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentPage, range, weekStart, refresh]);

  const handleRowClick = (staffId) => {
    setDetailRow(rows.find((r) => r.staffId === staffId) || null);
  };

  // Edit a single time entry's clock in/out. Closes the detail drawer and opens
  // the Scheduler-native single-entry editor (focused on just this record).
  const handleEditEntry = (track) => {
    setBackRow(detailRow);
    setDetailRow(null);
    setEditRecord(track);
  };

  // Edit the EVV record (clock-in/out timestamps + addresses) for an entry,
  // reusing the shared EVV editor from the EVV Records tab.
  const handleEditEvv = (track) => {
    setBackRow(detailRow);
    setDetailRow(null);
    setEvvRecord(track);
  };

  // Return from an editor to the caregiver's daily-entries detail (with fresh data).
  const backToDetail = () => {
    setEditRecord(null);
    setEvvRecord(null);
    if (backRow) setDetailRow(rows.find((r) => r.staffId === backRow.staffId) || backRow);
    setBackRow(null);
  };

  const tsExportRows = (data) => data.map((r) => [r.name, r.shifts, r.hours.toFixed(1), r.regular.toFixed(1), r.ot.toFixed(1), r.exceptions, r.status === "approved" ? "Approved" : "Pending"]);
  const exportTimesheetCsv = (data, label) => {
    const head = ["Caregiver", "Shifts", "Hours logged", "Regular", "Overtime", "Exceptions", "Status"];
    const csv = [head, ...tsExportRows(data)].map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `Timesheets_${label}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const exportTimesheetPdf = (data, label) => {
    const doc = new jsPDF();
    doc.setFontSize(13);
    doc.text(`Timesheets · ${label}`, 14, 16);
    doc.autoTable({
      head: [["Caregiver", "Shifts", "Hours", "Regular", "OT", "Exceptions", "Status"]],
      body: tsExportRows(data),
      startY: 22,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [23, 23, 23] },
    });
    doc.save(`Timesheets_${label}.pdf`);
  };

  // Run the chosen export for a user-selected period. Timesheets are fetched +
  // aggregated for that range (without disturbing the displayed week); EVV
  // records are fetched and written by the shared helper.
  const runExport = async ([from, to]) => {
    const target = exportTarget;
    if (!target) return;
    const label = `${from.format("YYYY-MM-DD")}_${to.format("YYYY-MM-DD")}`;
    try {
      if (target.kind === "evv") {
        const count = await downloadEvvRecords({ format: target.format, query: { dateFrom: from.format("YYYY-MM-DD"), dateTo: to.format("YYYY-MM-DD") } });
        if (!count) swal("No EVV records", "No EVV records for the selected period.", "info");
      } else {
        const recs = await fetchAttendanceRange([from, to]);
        const data = aggregateTimesheets({ attendanceStaff: recs }, otThreshold).filter((r) => r.hours > 0);
        if (!data.length) swal("No timesheets", "No timesheets for the selected period.", "info");
        else if (target.format === "pdf") exportTimesheetPdf(data, label);
        else exportTimesheetCsv(data, label);
      }
    } catch (err) {
      swal("Export failed", "Could not export. Please try again.", "error");
    } finally {
      setExportTarget(null);
    }
  };

  const headerExport = (
    <Menu shadow="md" width={190} position="bottom-end">
      <Menu.Target>
        <button type="button" className={css.tbBtn} style={{ height: 36 }}>
          <Icon name="download" size={15} /> Export <Icon name="chevronDown" size={13} />
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Timesheets</Menu.Label>
        <Menu.Item onClick={() => setExportTarget({ kind: "timesheets", format: "pdf" })}>Export as PDF</Menu.Item>
        <Menu.Item onClick={() => setExportTarget({ kind: "timesheets", format: "csv" })}>Export as CSV</Menu.Item>
        <Menu.Divider />
        <Menu.Label>EVV records</Menu.Label>
        <Menu.Item onClick={() => setExportTarget({ kind: "evv", format: "csv" })}>Export as CSV</Menu.Item>
        <Menu.Item onClick={() => setExportTarget({ kind: "evv", format: "pdf" })}>Export as PDF</Menu.Item>
        <Menu.Item onClick={() => setExportTarget({ kind: "evv", format: "json" })}>Export as JSON</Menu.Item>
        <Menu.Item onClick={() => setExportTarget({ kind: "evv", format: "xml" })}>Export as XML</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
  const headerPrimary = (
    <button type="button" className={`${css.tbBtn} ${css.tbBtnPrimary}`} style={{ height: 36 }} onClick={openAddModal}>
      <Icon name="plus" size={16} /> Add Missed Shift
    </button>
  );

  // Weekly calendar nav, rendered in the table header (left of the actions).
  const headerLeft = (
    <Popover opened={datePop} onChange={setDatePop} position="bottom-start" withinPortal shadow="md" radius={16}>
      <Popover.Target>
        <button
          type="button"
          className={css.tbBtn}
          style={{ height: 36 }}
          onClick={() => {
            setDpMonth(new Date(weekStart.getFullYear(), weekStart.getMonth(), 1));
            setDatePop((o) => !o);
          }}
        >
          <Icon name="calendar" size={15} /> Week of {weekLabel(weekStart)}
        </button>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <WeekPicker
          monthDate={dpMonth}
          selWeek={weekStart}
          mode="week"
          onMonthChange={setDpMonth}
          onPick={(d) => {
            setWeekStart(startOfWeek(d));
            setDatePop(false);
          }}
          onClose={() => setDatePop(false)}
        />
      </Popover.Dropdown>
    </Popover>
  );

  return (
    <Box>
      <div className={css.kpiGrid4}>
        <div className={css.kpi}>
          <div className={css.kpiRow}>
            <span className={css.kpiLabel}>Worked Hours</span>
            <span className={`${css.kpiIcon} ${css.info}`}><Icon name="clock" size={17} /></span>
          </div>
          <div className={css.kpiValue}>{kpis.hours.toFixed(1)}<span className={css.kpiUnit}>hrs</span></div>
          <div className={css.kpiMeta}>{kpis.total} caregiver{kpis.total === 1 ? "" : "s"} this period</div>
        </div>
        <div className={css.kpi}>
          <div className={css.kpiRow}>
            <span className={css.kpiLabel}>Overtime</span>
            <span className={`${css.kpiIcon} ${css.amber}`}><Icon name="trend" size={17} /></span>
          </div>
          <div className={css.kpiValue}>{(otAlerts ? kpis.ot : 0).toFixed(1)}<span className={css.kpiUnit}>hrs</span></div>
          <div className={css.kpiMeta}>{otAlerts ? `over ${otThreshold}h / week` : "alerts off"}</div>
        </div>
        <div className={css.kpi}>
          <div className={css.kpiRow}>
            <span className={css.kpiLabel}>Exceptions</span>
            <span className={`${css.kpiIcon} ${css.red}`}><Icon name="alert" size={17} /></span>
          </div>
          <div className={css.kpiValue}>{kpis.exceptions}</div>
          <div className={css.kpiMeta}>{kpis.exceptions ? "need review" : "all clear"}</div>
        </div>
        <div className={css.kpi}>
          <div className={css.kpiRow}>
            <span className={css.kpiLabel}>Approved</span>
            <span className={`${css.kpiIcon} ${css.violet}`}><Icon name="check" size={17} /></span>
          </div>
          <div className={css.kpiValue}>{kpis.approved}<span className={css.kpiUnit}>/{kpis.total}</span></div>
          <div className={css.kpiMeta}><strong>{kpis.total - kpis.approved}</strong> awaiting approval</div>
        </div>
      </div>

      <TimesheetsTable rows={rows} loading={loading} onRowClick={handleRowClick} onApproved={() => setRefresh((p) => !p)} headerLeft={headerLeft} headerExport={headerExport} headerPrimary={headerPrimary} showOvertime={otAlerts} />

      <TimesheetDetailDrawer
        opened={!!detailRow}
        row={detailRow}
        weekLabel={rangeLabel}
        onClose={() => setDetailRow(null)}
        onApproved={() => { setDetailRow(null); setRefresh((p) => !p); }}
        onAddMissed={() => { setAddPrefill(null); setDetailRow(null); openAddModal(); }}
        onAddMissedForShift={(prefill) => { setBackRow(detailRow); setAddPrefill(prefill); setDetailRow(null); openAddModal(); }}
        onEditEntry={handleEditEntry}
        onEditEvv={handleEditEvv}
        showOvertime={otAlerts}
        otThreshold={otThreshold}
      />
      <MissedShiftDrawer
        opened={isAddModalOpen}
        onClose={() => {
          closeAddModal();
          setAddPrefill(null);
          setRefresh((p) => !p);
          // Return to the caregiver's detail drawer if we came from it.
          if (backRow) { setDetailRow(rows.find((r) => r.staffId === backRow.staffId) || backRow); setBackRow(null); }
        }}
        staffId="all"
        prefill={addPrefill}
        currentPage={currentPage}
        range={range}
        onSaved={() => setRefresh((p) => !p)}
      />
      <TimeEntryEditDrawer
        opened={!!editRecord}
        record={editRecord}
        onClose={() => { setEditRecord(null); setBackRow(null); }}
        onSaved={() => setRefresh((p) => !p)}
        onBack={backRow ? backToDetail : undefined}
      />
      <EvvRecordDrawer
        opened={!!evvRecord}
        record={evvRecord}
        onClose={() => { setEvvRecord(null); setBackRow(null); }}
        onSaved={() => setRefresh((p) => !p)}
        onBack={backRow ? backToDetail : undefined}
      />
      <ExportPeriodModal
        opened={!!exportTarget}
        target={exportTarget}
        defaultStart={weekStart}
        onConfirm={runExport}
        onClose={() => setExportTarget(null)}
      />
    </Box>
  );
}
